import {
	QueryObserverOptions,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import { useAuth } from '../auth/useAuth'
import { BillingService } from '../../lib/api/billing.services'
import {
	ChangePlanQuotaDataProps,
	CreateBillingLinkDataResponse,
	GetPlanDataResponse,
	GetPlanProductDataResponse,
	PdfDataResponse,
	SubscribePlanDataProps,
} from '../../lib/api/types/billing.interfaces'
import { handleApiError } from '../../lib/utils/handleApiError'
import { DefaultGetParamsProps } from '../../lib/api/types/interfaces'
import toastCaller from '../../lib/utils/toastCaller'
import { useMemo } from 'react'
import { usePricingRegion } from './pricingHooks'
import { mapRegionalPlanProducts } from '../../lib/utils/mappers/regionalPlanMapper'

export const usePlan = (
	options?: Partial<QueryObserverOptions<GetPlanDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetPlanDataResponse>({
		queryKey: ['plan'],
		queryFn: () => BillingService.getPlan({}),
		enabled: isAuthenticated,
		...options,
	})
}

export const useUpdatePlanFeatures = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['updatePlanFeatures'],
		mutationFn: ({ value }: { value: number }) =>
			BillingService.updatePlanFeatures({ value }),
		onError: (error) => handleApiError(error),
		onSuccess: (_, { value }) => {
			queryClient.setQueryData(
				['plan'],
				(oldData: GetPlanDataResponse | undefined) => {
					if (oldData) {
						return {
							...oldData,
							data: {
								...oldData?.data,
								featuresEnabled: value,
							},
						}
					}
					return oldData
				},
			)
			toastCaller({
				type: 'success',
				message: 'The plan configuration has been successfully updated!',
				withClosePrevious: true,
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

// change plan quota
export const useChangePlanQuota = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['changePlanQuota'],
		mutationFn: ({ ...data }: ChangePlanQuotaDataProps) =>
			BillingService.changePlanQuota({ ...data }),
		onError: (error) => {
			if (
				(error as any)?.response?.data?.errors?.[0] ===
				'BILLING_INVOICE_NOT_FOUND'
			) {
				toastCaller({
					type: 'warning',
					message:
						'[DEV ERROR]: Please buy a subscription again to update the object in the database. After will be able to change your plan!',
					withClosePrevious: true,
				})
				return
			}

			handleApiError(error)
		},
		onSuccess: ({ data }) => {
			queryClient.invalidateQueries({ queryKey: ['plan'] })
			data?.data?.url
			toastCaller({
				type: data?.data?.url ? 'success' : 'warning',
				message: data?.data?.url
					? 'Plan quotas have been successfully updated!'
					: 'Stripe url not found. \n Plan Quota seems changed without payment.',
				withClosePrevious: true,
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

// updatePlanFeatures
export const useSubscribe = () => {
	const mutation = useMutation({
		mutationKey: ['createSubscribeLink'],
		mutationFn: (data: SubscribePlanDataProps) =>
			BillingService.createSubscribeLink(data),
		onError: (error) => handleApiError(error),
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useRenewPlan = () => {
	const mutation = useMutation({
		mutationKey: ['renewPlan'],
		mutationFn: () => BillingService.renewPlan(),
		onError: (error) => handleApiError(error),
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useCancelPlan = () => {
	const mutation = useMutation({
		mutationKey: ['cancelPlan'],
		mutationFn: () => BillingService.cancelPlan(),
		onError: (error) => handleApiError(error),
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useCreateChangePaymentLink = () => {
	const mutation = useMutation({
		mutationKey: ['createChangePaymentLink'],
		mutationFn: () => BillingService.createChangePaymentLink(),
		onError: (error) => handleApiError(error),
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useRetry = () => {
	const mutation = useMutation({
		mutationKey: ['createRetryLink'],
		mutationFn: (data: DefaultGetParamsProps) =>
			BillingService.createRetryLink(data),
		onError: (error) => handleApiError(error),
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useReceipt = (
	{ id }: DefaultGetParamsProps,
	options?: Partial<QueryObserverOptions<PdfDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	const query = useQuery<PdfDataResponse>({
		queryKey: ['receipt', id],
		queryFn: () => BillingService.getReceipt({ id }),
		enabled: isAuthenticated && Boolean(id),
		retry: 0,
		...options,
	})

	return query
}

export const useInvoice = (
	{ id }: DefaultGetParamsProps,
	options?: Partial<QueryObserverOptions<PdfDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	const query = useQuery<PdfDataResponse>({
		queryKey: ['invoice', id],
		queryFn: () => BillingService.getInvoice({ id }),
		enabled: isAuthenticated && Boolean(id),
		retry: 0,
		...options,
	})

	return query
}

export const usePlanProduct = (
	options?: Partial<QueryObserverOptions<GetPlanProductDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetPlanProductDataResponse>({
		queryKey: ['planProduct'],
		queryFn: () => BillingService.getPlanProduct(),
		enabled: isAuthenticated,
		...options,
	})
}

export const useRegionalPlanProducts = ({ enabled = true } = {}) => {
	const { isAuthenticated } = useAuth()
	const queryEnabled = enabled && isAuthenticated
	const productsQuery = usePlanProduct({ enabled: queryEnabled })
	const regionQuery = usePricingRegion({ enabled: queryEnabled, retry: false })
	const error = productsQuery.error

	const data = useMemo(() => {
		if (error || !productsQuery.data?.success || regionQuery.isLoading)
			return []
		return mapRegionalPlanProducts(
			productsQuery.data.data,
			regionQuery.error ? undefined : regionQuery.data,
		)
	}, [
		productsQuery.data,
		regionQuery.data,
		regionQuery.error,
		regionQuery.isLoading,
		error,
	])

	return {
		data,
		isLoading: productsQuery.isLoading || regionQuery.isLoading,
		isError: Boolean(error),
		error,
		refetch: () =>
			Promise.all([productsQuery.refetch(), regionQuery.refetch()]),
	}
}
